/**
 * Textos de la política de cookies y del aviso de cookies (es / eu / en).
 * Reflejan lo que la aplicación usa realmente: si se añade o quita una herramienta, actualizar aquí.
 * Pendiente: revisión de las versiones en euskera e inglés por una persona del equipo.
 */
export interface CookieRow {
  name: string;
  kind: string;
  purpose: string;
  duration: string;
}

export interface CookieCopy {
  banner: { title: string; text: string; more: string; accept: string; reject: string };
  page: {
    crumb: string;
    title: string;
    lead: string;
    updated: string;
    whatH: string;
    whatP: string;
    techH: string;
    techP: string;
    techRows: CookieRow[];
    anaH: string;
    anaP: string;
    anaRows: CookieRow[];
    thirdH: string;
    thirdItems: { name: string; text: string }[];
    cols: { name: string; kind: string; purpose: string; duration: string };
    manageH: string;
    manageP: string;
    status: { accepted: string; rejected: string; none: string };
    accept: string;
    reject: string;
    browserP: string;
    contactH: string;
    contactP: string;
    contactCta: string;
  };
}

export const COOKIE_COPY: Record<string, CookieCopy> = {
  es: {
    banner: {
      title: 'Cookies',
      text: 'Usamos almacenamiento técnico imprescindible para que la reserva funcione. Con tu permiso, también usamos analítica anónima (PostHog) para saber cómo mejorar la web. Puedes cambiar de opinión cuando quieras.',
      more: 'Más información',
      accept: 'Aceptar analítica',
      reject: 'Solo las necesarias',
    },
    page: {
      crumb: 'Inicio',
      title: 'Política de cookies',
      lead: 'Qué guardamos en tu navegador, para qué y cómo puedes controlarlo.',
      updated: 'Última actualización: octubre de 2026',
      whatH: 'Qué son las cookies',
      whatP:
        'Las cookies y el almacenamiento local son pequeños archivos o datos que la web guarda en tu navegador. Algunos son imprescindibles para que el servicio funcione; otros nos ayudan a entender cómo se usa la web.',
      techH: 'Técnicas (siempre activas)',
      techP:
        'Son necesarias para recordar tu idioma, el avance de tu reserva y tu elección sobre las cookies. No requieren consentimiento y no se usan para publicidad ni para seguirte.',
      techRows: [
        { name: 'sagardobus-cookie-consent', kind: 'Cookie propia', purpose: 'Recuerda si has aceptado o rechazado la analítica.', duration: '6 meses' },
        { name: 'language', kind: 'Almacenamiento local', purpose: 'Idioma elegido en la web.', duration: 'Hasta que lo borres' },
        { name: 'menu', kind: 'Almacenamiento local', purpose: 'Copia del menú de navegación para cargar más rápido.', duration: 'Hasta que lo borres' },
        { name: 'bookingInformation, sharedData', kind: 'Almacenamiento local', purpose: 'Datos de la reserva en curso para que no se pierdan al navegar o volver del pago.', duration: 'Hasta finalizar la reserva' },
      ],
      anaH: 'Analítica (solo si la aceptas)',
      anaP:
        'Usamos PostHog (servidores en la Unión Europea) para medir visitas y en qué paso se abandona una reserva. No registramos nombre, correo, teléfono ni DNI, y no se usa para publicidad. Si rechazas, no se carga nada de esto.',
      anaRows: [
        { name: 'ph_…_posthog', kind: 'Cookie y almacenamiento local (PostHog)', purpose: 'Identificador anónimo de visitante para estadísticas.', duration: '1 año' },
        { name: 'analytics_session_id, analytics_booking_funnel_id', kind: 'Almacenamiento de sesión', purpose: 'Agrupar los pasos de una misma visita y de un mismo intento de reserva.', duration: 'Hasta cerrar la pestaña' },
      ],
      thirdH: 'Servicios de terceros',
      thirdItems: [
        { name: 'Redsys (pago con tarjeta)', text: 'Al pagar te redirigimos a la pasarela de tu banco/Redsys, que usa sus propias cookies técnicas y de seguridad para completar el pago. Consulta su política en su web.' },
        { name: 'Google Fonts', text: 'Las tipografías se cargan desde servidores de Google, que reciben tu dirección IP al descargarlas. No instalan cookies de seguimiento en nuestra web.' },
      ],
      cols: { name: 'Nombre', kind: 'Tipo', purpose: 'Finalidad', duration: 'Duración' },
      manageH: 'Gestiona tu elección',
      manageP: 'Estado actual de la analítica:',
      status: { accepted: 'Aceptada', rejected: 'Rechazada', none: 'Sin decidir' },
      accept: 'Aceptar analítica',
      reject: 'Rechazar analítica',
      browserP:
        'También puedes borrar o bloquear las cookies desde la configuración de tu navegador; ten en cuenta que, si bloqueas las técnicas, la reserva podría no funcionar correctamente.',
      contactH: '¿Dudas?',
      contactP: 'Si tienes cualquier pregunta sobre cómo tratamos tus datos, escríbenos.',
      contactCta: 'Contacto',
    },
  },
  eu: {
    banner: {
      title: 'Cookieak',
      text: 'Erreserbak funtziona dezan ezinbestekoa den biltegiratze teknikoa erabiltzen dugu. Zure baimenarekin, analitika anonimoa ere erabiltzen dugu (PostHog), webgunea hobetzeko. Iritzia noiznahi alda dezakezu.',
      more: 'Informazio gehiago',
      accept: 'Onartu analitika',
      reject: 'Beharrezkoak soilik',
    },
    page: {
      crumb: 'Hasiera',
      title: 'Cookien politika',
      lead: 'Zer gordetzen dugun zure nabigatzailean, zertarako eta nola kontrola dezakezun.',
      updated: 'Azken eguneratzea: 2026ko urria',
      whatH: 'Zer dira cookieak',
      whatP:
        'Cookieak eta biltegiratze lokala webguneak zure nabigatzailean gordetzen dituen fitxategi edo datu txikiak dira. Batzuk ezinbestekoak dira zerbitzuak funtziona dezan; beste batzuek webgunea nola erabiltzen den ulertzen laguntzen digute.',
      techH: 'Teknikoak (beti aktibo)',
      techP:
        'Zure hizkuntza, erreserbaren aurrerapena eta cookieei buruzko aukera gogoratzeko behar dira. Ez dute baimenik behar eta ez dira publizitaterako erabiltzen, ezta zu jarraitzeko ere.',
      techRows: [
        { name: 'sagardobus-cookie-consent', kind: 'Cookie propioa', purpose: 'Analitika onartu ala baztertu duzun gogoratzen du.', duration: '6 hilabete' },
        { name: 'language', kind: 'Biltegiratze lokala', purpose: 'Webgunean aukeratutako hizkuntza.', duration: 'Zuk ezabatu arte' },
        { name: 'menu', kind: 'Biltegiratze lokala', purpose: 'Nabigazio-menuaren kopia, azkarrago kargatzeko.', duration: 'Zuk ezabatu arte' },
        { name: 'bookingInformation, sharedData', kind: 'Biltegiratze lokala', purpose: 'Abian dagoen erreserbaren datuak, nabigatzean edo ordainketatik itzultzean galdu ez daitezen.', duration: 'Erreserba amaitu arte' },
      ],
      anaH: 'Analitika (onartzen baduzu soilik)',
      anaP:
        'PostHog erabiltzen dugu (Europar Batasuneko zerbitzariak) bisitak neurtzeko eta erreserba zein urratsetan uzten den jakiteko. Ez dugu izenik, posta elektronikorik, telefonorik edo NANrik erregistratzen, eta ez da publizitaterako erabiltzen. Baztertzen baduzu, ez da hau guztia kargatzen.',
      anaRows: [
        { name: 'ph_…_posthog', kind: 'Cookie eta biltegiratze lokala (PostHog)', purpose: 'Bisitari anonimoaren identifikatzailea, estatistikak egiteko.', duration: 'Urte 1' },
        { name: 'analytics_session_id, analytics_booking_funnel_id', kind: 'Saioaren biltegiratzea', purpose: 'Bisita beraren eta erreserba-saiakera beraren urratsak elkartzeko.', duration: 'Fitxa itxi arte' },
      ],
      thirdH: 'Hirugarrenen zerbitzuak',
      thirdItems: [
        { name: 'Redsys (txartelez ordaintzea)', text: 'Ordaintzean zure bankuaren/Redsysen pasabidera bideratzen zaitugu; honek bere cookie tekniko eta segurtasunekoak erabiltzen ditu ordainketa burutzeko. Kontsultatu haien politika haien webgunean.' },
        { name: 'Google Fonts', text: 'Letra-tipoak Googleren zerbitzarietatik kargatzen dira, eta hauek zure IP helbidea jasotzen dute deskargatzean. Ez dute jarraipen-cookierik instalatzen gure webgunean.' },
      ],
      cols: { name: 'Izena', kind: 'Mota', purpose: 'Helburua', duration: 'Iraupena' },
      manageH: 'Kudeatu zure aukera',
      manageP: 'Analitikaren egungo egoera:',
      status: { accepted: 'Onartuta', rejected: 'Baztertuta', none: 'Erabaki gabe' },
      accept: 'Onartu analitika',
      reject: 'Baztertu analitika',
      browserP:
        'Cookieak zure nabigatzailearen ezarpenetatik ere ezaba edo blokea ditzakezu; kontuan izan teknikoak blokeatzen badituzu erreserbak ez duela behar bezala funtzionatuko.',
      contactH: 'Zalantzarik?',
      contactP: 'Zure datuak nola tratatzen ditugunari buruzko edozein galdera baduzu, idatzi iezaguzu.',
      contactCta: 'Kontaktua',
    },
  },
  en: {
    banner: {
      title: 'Cookies',
      text: 'We use essential technical storage so your booking works. With your permission we also use anonymous analytics (PostHog) to help us improve the website. You can change your mind at any time.',
      more: 'More information',
      accept: 'Accept analytics',
      reject: 'Necessary only',
    },
    page: {
      crumb: 'Home',
      title: 'Cookie policy',
      lead: 'What we store in your browser, why, and how you can control it.',
      updated: 'Last updated: October 2026',
      whatH: 'What are cookies',
      whatP:
        'Cookies and local storage are small files or pieces of data that a website saves in your browser. Some are essential for the service to work; others help us understand how the website is used.',
      techH: 'Technical (always on)',
      techP:
        'They are needed to remember your language, the progress of your booking and your cookie choice. They do not require consent and are not used for advertising or to track you.',
      techRows: [
        { name: 'sagardobus-cookie-consent', kind: 'First-party cookie', purpose: 'Remembers whether you accepted or rejected analytics.', duration: '6 months' },
        { name: 'language', kind: 'Local storage', purpose: 'Language chosen on the website.', duration: 'Until you clear it' },
        { name: 'menu', kind: 'Local storage', purpose: 'Copy of the navigation menu for faster loading.', duration: 'Until you clear it' },
        { name: 'bookingInformation, sharedData', kind: 'Local storage', purpose: 'Data of the booking in progress so it is not lost when navigating or returning from payment.', duration: 'Until the booking is completed' },
      ],
      anaH: 'Analytics (only if you accept)',
      anaP:
        'We use PostHog (servers in the European Union) to measure visits and the step at which a booking is abandoned. We do not record name, email, phone or ID number, and it is not used for advertising. If you reject, none of this is loaded.',
      anaRows: [
        { name: 'ph_…_posthog', kind: 'Cookie and local storage (PostHog)', purpose: 'Anonymous visitor identifier for statistics.', duration: '1 year' },
        { name: 'analytics_session_id, analytics_booking_funnel_id', kind: 'Session storage', purpose: 'Group the steps of one visit and one booking attempt.', duration: 'Until the tab is closed' },
      ],
      thirdH: 'Third-party services',
      thirdItems: [
        { name: 'Redsys (card payment)', text: 'When paying you are redirected to your bank/Redsys payment gateway, which uses its own technical and security cookies to complete the payment. See their website for their policy.' },
        { name: 'Google Fonts', text: 'Fonts are loaded from Google servers, which receive your IP address when downloading them. They do not set tracking cookies on our website.' },
      ],
      cols: { name: 'Name', kind: 'Type', purpose: 'Purpose', duration: 'Duration' },
      manageH: 'Manage your choice',
      manageP: 'Current analytics status:',
      status: { accepted: 'Accepted', rejected: 'Rejected', none: 'Not decided' },
      accept: 'Accept analytics',
      reject: 'Reject analytics',
      browserP:
        'You can also delete or block cookies from your browser settings; note that if you block the technical ones, booking may not work properly.',
      contactH: 'Questions?',
      contactP: 'If you have any question about how we handle your data, get in touch.',
      contactCta: 'Contact',
    },
  },
};

export function cookieCopy(lang: string): CookieCopy {
  return COOKIE_COPY[lang] ?? COOKIE_COPY['es'];
}
